import { CheckCircle, AlertCircle, XCircle, Clock, Zap } from 'lucide-react'
import type { ReactFlowTraceExport } from '../../../lib/api'
import type { TeamHealth } from './types'

interface ExecutionStep {
  label: string
  status: 'success' | 'warning' | 'error' | 'skipped'
  detail: string
}

function buildNarrativeSteps(trace: ReactFlowTraceExport): ExecutionStep[] {
  const nodes = trace.nodes
  const steps: ExecutionStep[] = []

  const hasResearch = nodes.some((n) =>
    ['tavily_researcher', 'perplexity_researcher', 'perplexity_ask', 'perplexity_reason'].some(
      (r) => n.data.agent_role?.includes(r) || n.data.label?.toLowerCase().includes('research')
    )
  )

  if (hasResearch) {
    const researchNodes = nodes.filter((n) => n.data.phase === 'researching')
    const allOk = researchNodes.every((n) => n.data.status === 'success')
    steps.push({
      label: 'Investigacion',
      status: allOk ? 'success' : 'warning',
      detail: researchNodes.length > 0 ? `${researchNodes.length} fuentes consultadas` : 'Completada',
    })
  }

  const writerNodes = nodes.filter((n) => n.data.agent_role === 'writer')
  if (writerNodes.length > 0) {
    const iteraciones = writerNodes.length
    steps.push({
      label: 'Borrador generado',
      status: 'success',
      detail: iteraciones > 1 ? `${iteraciones} iteraciones` : 'Primera iteracion',
    })
  }

  const brandNodes = nodes.filter((n) => n.data.agent_role === 'brand_guard')
  if (brandNodes.length > 0) {
    const passed = brandNodes.every((n) => n.data.status === 'success')
    steps.push({
      label: 'Guard de marca',
      status: passed ? 'success' : 'warning',
      detail: passed ? 'Sin violaciones' : 'Ajustes requeridos',
    })
  }

  const validatorNodes = nodes.filter((n) => n.data.agent_role === 'validator')
  if (validatorNodes.length > 0) {
    const allOk = validatorNodes.every((n) => n.data.status === 'success')
    steps.push({
      label: 'Validacion triple',
      status: allOk ? 'success' : 'warning',
      detail: allOk
        ? 'Disruption, Cuantitativo, Alineamiento OK'
        : 'Una o mas revisiones requeridas',
    })
  }

  const criticNodes = nodes.filter((n) => n.data.agent_role === 'critic')
  if (criticNodes.length > 0) {
    const lastCritic = criticNodes[criticNodes.length - 1]
    const preview = lastCritic.data.output_preview ?? ''
    const scoreMatch = preview.match(/(\d+\.?\d*)\s*\/\s*10/)
    const score = scoreMatch ? scoreMatch[1] : null
    steps.push({
      label: 'Calificacion del Critico',
      status: lastCritic.data.status === 'success' ? 'success' : 'warning',
      detail: score ? `${score}/10 — Aprobado` : 'Evaluacion completada',
    })
  }

  const visualNodes = nodes.filter((n) => n.data.agent_role === 'image_prompter')
  if (visualNodes.length > 0) {
    steps.push({
      label: 'Prompts visuales',
      status: 'success',
      detail: `${visualNodes.length} prompt(s) generado(s)`,
    })
  }

  return steps
}

export function deriveTeamHealthFromTrace(trace: ReactFlowTraceExport): Record<string, TeamHealth> {
  const health: Record<string, TeamHealth> = {}
  const teamRoles: Record<string, string[]> = {
    content: ['planner', 'writer', 'critic', 'brand_guard', 'validator'],
    visual: ['image_prompter', 'brand_guard', 'validator'],
    strategy: ['researcher'],
  }
  for (const [team, roles] of Object.entries(teamRoles)) {
    const teamNodes = trace.nodes.filter((n) =>
      roles.some((r) => n.data.agent_role?.includes(r))
    )
    if (teamNodes.length === 0) {
      health[team] = 'idle'
    } else if (teamNodes.some((n) => n.data.status === 'error')) {
      health[team] = 'error'
    } else if (teamNodes.some((n) => n.data.status !== 'success')) {
      health[team] = 'warning'
    } else {
      health[team] = 'healthy'
    }
  }
  return health
}

const STEP_ICONS = {
  success: CheckCircle,
  warning: AlertCircle,
  error: XCircle,
  skipped: Clock,
}

const STEP_COLORS = {
  success: 'text-emerald-600',
  warning: 'text-amber-500',
  error: 'text-red-500',
  skipped: 'text-slate-400',
}

interface ExecutionSummaryPanelProps {
  trace: ReactFlowTraceExport | null | undefined
  isLoading: boolean
  onViewTechnical: () => void
}

export function ExecutionSummaryPanel({ trace, isLoading, onViewTechnical }: ExecutionSummaryPanelProps) {
  if (isLoading) {
    return (
      <div className="card p-5 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-3 bg-slate-100 rounded" />
          ))}
        </div>
      </div>
    )
  }

  if (!trace) {
    return (
      <div className="card p-5 border-dashed">
        <p className="text-sm text-slate-400 text-center py-4">
          No hay ejecuciones recientes. Genera contenido para ver el reporte aqui.
        </p>
      </div>
    )
  }

  const steps = buildNarrativeSteps(trace)
  const stats = trace.metadata?.stats
  const durationSecs = stats?.execution_time_ms ? (stats.execution_time_ms / 1000).toFixed(0) : null

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Ultima Ejecucion</h3>
          <p className="text-xs text-slate-400 mt-0.5">{trace.metadata.project_name}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          {durationSecs && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {durationSecs}s
            </span>
          )}
          {(stats?.total_tokens ?? 0) > 0 && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {(stats?.total_tokens ?? 0).toLocaleString()} tokens
            </span>
          )}
        </div>
      </div>

      <ul className="space-y-2">
        {steps.map((step, i) => {
          const Icon = STEP_ICONS[step.status]
          return (
            <li key={i} className="flex items-start gap-3">
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${STEP_COLORS[step.status]}`} />
              <div>
                <span className="text-sm font-medium text-slate-700">{step.label}</span>
                <span className="text-xs text-slate-400 ml-2">{step.detail}</span>
              </div>
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        onClick={onViewTechnical}
        className="mt-4 text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
      >
        Ver detalle tecnico en el canvas
        <span aria-hidden>→</span>
      </button>
    </div>
  )
}
